import { useEffect, useId, useRef, useState } from 'react';
import { LEARNING_RESOURCE_SECTIONS } from '../constants/learningResources';
import {
  BackArrowIcon,
  ChevronRightIcon,
  createFocusTrap,
  XIcon,
} from '@alchemiesolns/ui';
import { Collapsible } from './Collapsible';
import './LearningResourcesPanel.css';

interface LearningResourcesPanelProps {
  open: boolean;
  onClose: () => void;
}

function FormattedDescription({ text }: { text: string }) {
  const paragraphs = text.trim().split(/\n\n+/).filter(Boolean);
  if (paragraphs.length === 0) {
    return null;
  }
  return (
    <>
      {paragraphs.map((para, i) => (
        <p key={i} className="learning-resources-desc">
          {para}
        </p>
      ))}
    </>
  );
}

function YouTubeEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <div className="learning-resources-video-wrap">
      <iframe
        title={title}
        src={`https://www.youtube.com/embed/${videoId}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
}

export function LearningResourcesPanel({ open, onClose }: LearningResourcesPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const listTitleId = useId();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const activeSection =
    activeSectionId === null
      ? null
      : LEARNING_RESOURCE_SECTIONS.find((s) => s.id === activeSectionId) ?? null;

  useEffect(() => {
    if (!open) return;
    const el = panelRef.current;
    if (!el) return;
    return createFocusTrap(el);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActiveSectionId(null);
    }
  }, [open]);

  useEffect(() => {
    setTranscriptOpen(false);
  }, [activeSectionId]);

  if (!open) {
    return null;
  }

  const detailHeadingId = activeSection ? `learning-detail-${activeSection.id}` : listTitleId;

  return (
    <div
      ref={panelRef}
      className="learning-resources-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby={activeSection ? detailHeadingId : listTitleId}
    >
      <div
        className="learning-resources-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="learning-resources-sheet">
        <div
          className={
            activeSection
              ? 'learning-resources-sheet-header learning-resources-sheet-header--detail'
              : 'learning-resources-sheet-header'
          }
        >
          {activeSection ? (
            <>
              <button
                type="button"
                className="learning-resources-back"
                onClick={() => setActiveSectionId(null)}
                aria-label="Back to topics"
              >
                <BackArrowIcon width={22} height={22} />
                <span>Back</span>
              </button>
              <button
                type="button"
                className="learning-resources-close"
                onClick={onClose}
                aria-label="Close resources panel"
              >
                <XIcon width={20} height={20} />
              </button>
            </>
          ) : (
            <>
              <h2 id={listTitleId} className="learning-resources-title">
                Getting Started
              </h2>
              <button
                type="button"
                className="learning-resources-close"
                onClick={onClose}
                aria-label="Close resources panel"
              >
                <XIcon width={20} height={20} />
              </button>
            </>
          )}
        </div>

        <div className="learning-resources-body">
          {activeSection ? (
            <div className="learning-resources-detail">
              <h2 id={detailHeadingId} className="learning-resources-detail-title">
                {activeSection.heading}
              </h2>
              <div className="learning-resources-video-block">
                <YouTubeEmbed
                  videoId={activeSection.youtubeId}
                  title={`Video: ${activeSection.heading}`}
                />
              </div>
              {activeSection.sectionIntro.trim() !== '' && (
                <p className="learning-resources-section-intro">{activeSection.sectionIntro}</p>
              )}
              <div className="learning-resources-transcript">
                <Collapsible
                  title="View transcript"
                  isOpen={transcriptOpen}
                  onToggle={() => setTranscriptOpen((o) => !o)}
                  contentId={`learning-transcript-${activeSection.id}`}
                  ariaLabel="Transcript"
                >
                  <div className="learning-resources-detail-blocks">
                    {activeSection.subsections.map((item, idx) => (
                      <section key={`${activeSection.id}-${idx}`} className="learning-resources-detail-block">
                        <h3 className="learning-resources-subsection-title">{item.title}</h3>
                        <FormattedDescription text={item.description} />
                      </section>
                    ))}
                  </div>
                </Collapsible>
              </div>
            </div>
          ) : (
            <ul className="learning-resources-topic-list">
              {LEARNING_RESOURCE_SECTIONS.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    className="learning-resources-topic-row"
                    onClick={() => setActiveSectionId(section.id)}
                  >
                    <span className="learning-resources-topic-row-label">{section.heading}</span>
                    <ChevronRightIcon
                      width={18}
                      height={18}
                      className="learning-resources-topic-row-chevron"
                      aria-hidden
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
